import { Entity } from './entity';
import { inject } from '@angular/core';
import { ViewsService } from '../service/views.service';
import { AuthService } from '../service/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { LoadViewDialogComponent } from '../shared/load-view-dialog/load-view-dialog.component';
import { DeleteViewDialogComponent } from '../shared/delete-view-dialog/delete-view-dialog.component';
import { ShareViewComponent } from '../shared/share-view/share-view.component';
import { unwrapUserData } from '../shared/utils/unwrap-user-data';

let moment = require('moment');


export class EntityList extends Entity {

    public userRole: string | null = null;

    public gridApi;
    public columnApi;

    public savedNamedViews = null;

    waitingForServer = false;
    customizeColumnsUi = false;

    // *** Saving Views ***
    showNameSavedView = false;  // shows the dialog to save a named view
    nameOfSavedView: string | null = null; // if you are showing the ability to name a view what name did the customer type
    savedUserViews: any[] = []; // list of all the previously saved user views
    savedViewToLoadName: string | null = null;  // value of the drop down of previously saved views
    loadSavedReportVisible = false; // Shows the select drop down to load a saved report

    selectedCols: any | null = null;
    allColumns: any[] = [];

    private _saveViewSnackBar: any;

    // Services for view management
    protected viewsService = inject(ViewsService);
    protected authService = inject(AuthService);
    // Note: dialog and snackBar are inherited from Entity base class

    // Entity type - SHOULD be set by derived class for view management (e.g., 'contracts', 'people', etc.)
    // Can be undefined for components that don't use view management
    protected entityType?: string;

    // Session state management
    public hasUnsavedChanges = false;
    public filtersApplied = false;
    protected sessionStorageKey = ''; // Protected so derived classes can check for session state
    protected suspendSessionTracking = false; // Temporarily disable tracking during save operations (protected so derived classes can access)
    private isLoadingNamedView = false; // Flag to prevent session state restoration during named view load
    private sessionTrackingRestoreTimeout: any = null; // Timeout handle for canceling pending session tracking restoration

    constructor() {

        super();
    }

    /**
     * Initializes session storage for this entity type
     * Sets up the key but does NOT load session state automatically.
     * Session state loading is handled by completeInitialization() in derived classes
     * to allow proper sequencing with saved views and auto-sizing.
     */
    protected initializeSessionStorage(): void {
      if (this.entityType && !this.sessionStorageKey) {
        this.sessionStorageKey = `grid_session_${this.entityType}`;
      }

      // Don't automatically load state here - let completeInitialization() decide
      // whether to load saved view, auto-size, or restore session state
    }

    /**
     * Marks the current session as having unsaved modifications
     */
    public markSessionAsModified(): void {
      // Don't track changes if we're in the middle of a save operation
      if (this.suspendSessionTracking) {
        return;
      }

      this.hasUnsavedChanges = true;
      this.saveSessionState();
    }

    /**
     * Saves the current grid state to session storage
     */
    private saveSessionState(): void {
      if (!this.gridApi || !this.sessionStorageKey) {
        return;
      }

      const state = {
        columnState: this.gridApi.getColumnState(),
        filterModel: this.gridApi.getFilterModel(),
        columnDefs: this.selectedCols, // Save the actual column definitions
        timestamp: Date.now(),
        hasUserModifications: true // Flag to indicate this state contains real user changes
      };

      sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(state));
    }

    /**
     * Loads the grid state from session storage
     */
    protected loadSessionState(): void {
      if (!this.sessionStorageKey) {
        return;
      }

      const savedState = sessionStorage.getItem(this.sessionStorageKey);

      if (savedState) {
        try {
          const state = JSON.parse(savedState);

          // Restore column definitions first (before grid is ready)
          if (state.columnDefs) {
            // Get available columns from the component (these have valueGetters and other functions)
            const availableColumns = (this as any).availableColumns || [];

            // Restore function properties (valueGetter, valueFormatter, etc.) from availableColumns
            const restoredColumnDefs = state.columnDefs.map((savedCol: any) => {
              // Find the original column definition
              const originalCol = availableColumns.find((col: any) => col.colId === savedCol.colId);

              if (originalCol) {
                // Only restore function properties, don't touch anything else
                const restoredCol = { ...savedCol };
                if (typeof originalCol.valueGetter === 'function') {
                  restoredCol.valueGetter = originalCol.valueGetter;
                }
                if (typeof originalCol.valueFormatter === 'function') {
                  restoredCol.valueFormatter = originalCol.valueFormatter;
                }
                if (typeof originalCol.valueSetter === 'function') {
                  restoredCol.valueSetter = originalCol.valueSetter;
                }
                if (typeof originalCol.cellRenderer === 'function') {
                  restoredCol.cellRenderer = originalCol.cellRenderer;
                }
                // Restore string cellRenderer (like 'agGroupCellRenderer' for master/detail expand arrow)
                // This ensures session state preserves the expand arrow functionality
                if (typeof originalCol.cellRenderer === 'string' && !restoredCol.cellRenderer) {
                  restoredCol.cellRenderer = originalCol.cellRenderer;
                }
                return restoredCol;
              }

              return savedCol;
            });

            this.selectedCols = restoredColumnDefs;

            // If grid is ready, apply the restored column definitions
            if (this.gridApi) {
              this.gridApi.setGridOption('columnDefs', restoredColumnDefs);
            }
          }

          // If grid is ready, apply column state and filters
          if (this.gridApi) {
            // Apply column STATE (widths, order, visibility) after a brief delay
            // to let columnDefs update settle
            if (state.columnState) {
              setTimeout(() => {
                this.gridApi.applyColumnState({
                  state: state.columnState,
                  applyOrder: true
                });
              }, 100);
            }

            // Apply filter model
            // Note: If called from firstDataRendered event, data is already loaded
            if (state.filterModel && Object.keys(state.filterModel).length > 0) {
              this.gridApi.setFilterModel(state.filterModel);
              // Small delay for updateFiltersAppliedStatus to ensure filter UI is updated
              setTimeout(() => {
                this.updateFiltersAppliedStatus();
              }, 100);
            } else {
              this.updateFiltersAppliedStatus();
            }
          }

          this.hasUnsavedChanges = true;
        } catch (error) {
          console.error('[loadSessionState] Error loading session state:', error);
        }
      }
    }

    /**
     * Clears the session state (called when loading a saved view)
     */
    public clearSessionState(): void {
      if (this.sessionStorageKey) {
        sessionStorage.removeItem(this.sessionStorageKey);
      }
      this.hasUnsavedChanges = false;
    }

    /**
     * Checks if filters are currently applied to the grid
     */
    public updateFiltersAppliedStatus(): void {
      if (this.gridApi) {
        const filterModel = this.gridApi.getFilterModel();
        this.filtersApplied = Object.keys(filterModel).length > 0;
      }
    }

    /**
     * Removes all filters from the grid
     */
    public removeAllFilters(): void {
      if (this.gridApi) {
        this.gridApi.setFilterModel(null);
        this.updateFiltersAppliedStatus();
        this.markSessionAsModified();
      }
    }



    
  refreshGridList() {
    // this.gridApi.sizeColumnsToFit();
  }

  refreshGrid() {
    var params = {
      force: true,
      suppressFlash: false,
    };
    this.gridApi.refreshCells(params);
  }  


  onGridReady(params) {
    this.gridApi = params.api;
    this.columnApi = params.api;

    // Initialize session storage key
    this.initializeSessionStorage();

    // Start with session tracking SUSPENDED
    // Derived classes will handle initialization via completeInitialization()
    // and will enable tracking when ready
    this.suspendSessionTracking = true;

    // Set up listeners for grid changes to track unsaved changes
    this.gridApi.addEventListener('filterChanged', () => {
      this.updateFiltersAppliedStatus();
      this.markSessionAsModified();
    });

    this.gridApi.addEventListener('columnResized', (event: any) => {
      if (event.finished) {
        this.markSessionAsModified();
      }
    });

    this.gridApi.addEventListener('columnMoved', () => {
      this.markSessionAsModified();
    });

    this.gridApi.addEventListener('columnVisible', () => {
      this.markSessionAsModified();
    });

    this.gridApi.addEventListener('columnPinned', () => {
      this.markSessionAsModified();
    });

    this.gridApi.addEventListener('sortChanged', () => {
      this.markSessionAsModified();
    });
  }

  autoSizeAll() {
    if (this.gridApi) {
      // Suspend session tracking during auto-size to prevent it from being saved
      this.suspendSessionTracking = true;

      // Get all column IDs
      const allColumnIds: string[] = [];
      this.gridApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });

      // Auto-size all columns based on their IDs
      this.gridApi.autoSizeColumns(allColumnIds);

      // Enable session tracking after a delay to let ALL resize events finish
      // AG Grid fires columnResized and columnMoved events asynchronously after auto-size
      // Store the timeout handle so it can be canceled if a view is loaded
      this.sessionTrackingRestoreTimeout = setTimeout(() => {
        this.suspendSessionTracking = false;
        this.sessionTrackingRestoreTimeout = null;
      }, 1000); // Wait for all resize events to complete
    }
  }

      // This is the filter.   Needs a new name.
    onSearchChange(filterValue) {
      this.gridApi.setGridOption('quickFilterText', filterValue);
    }

    findTextInTableData(filterValue) {
      this.gridApi.setGridOption('quickFilterText', filterValue);
    }

    /**
     * Saves the current grid state as the default view for this entity type.
     * Uses AG Grid's official getState() API to capture complete grid state.
     */
    async saveView() {
      if (!this.gridApi) {
        this.snackBar.open('Grid not ready', 'Close', { duration: 3000 });
        return;
      }

      if (!this.entityType) {
        console.error('EntityList: entityType not set in derived class');
        this.snackBar.open('Configuration error: entity type not set', 'Close', { duration: 5000 });
        return;
      }

      try {
        // Suspend session tracking during save to prevent re-triggering unsaved changes
        this.suspendSessionTracking = true;

        const cognitoUser = await this.authService.getCurrentUser();
        if (!cognitoUser) {
          this.snackBar.open('Not authenticated', 'Close', { duration: 3000 });
          return;
        }

        const userId = await this.authService.getUserEmail();
        if (!userId) {
          this.snackBar.open('Could not retrieve user email', 'Close', { duration: 3000 });
          return;
        }

        // Use AG Grid's official getState() API - captures EVERYTHING including column widths
        const gridState = this.gridApi.getState();

        const viewData = {
          gridState: gridState,  // New format using AG Grid's official state API
          columnDefs: this.selectedCols,  // Save the column definitions (which columns are visible)
          // Keep old format for backward compatibility with existing views
          savedViewData: this.gridApi.getColumnState(),
          filterModel: gridState.filter?.filterModel || {}
        };

        // Save as default view using entityType as viewId
        await lastValueFrom(
          this.viewsService.saveListView(environment.org, userId, this.entityType, viewData)
        );

        // Clear unsaved changes flag since we just saved
        this.clearSessionState();

        this.snackBar.open('Default view saved successfully!', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      } catch (error) {
        console.error('Error saving default view:', error);
        this.snackBar.open('Failed to save view', 'Close', { duration: 5000 });
      } finally {
        // Always resume session tracking, even if save fails
        this.suspendSessionTracking = false;
      }
    }

    /**
     * Saves the current grid state as a named view.
     * User must provide a name via this.nameOfSavedView before calling.
     */
    async saveNamedViewNow() {
      if (!this.nameOfSavedView || this.nameOfSavedView.trim() === '') {
        this.snackBar.open('Please enter a name for this view', 'Close', { duration: 3000 });
        return;
      }

      if (!this.gridApi) {
        this.snackBar.open('Grid not ready', 'Close', { duration: 3000 });
        return;
      }

      if (!this.entityType) {
        console.error('EntityList: entityType not set in derived class');
        this.snackBar.open('Configuration error: entity type not set', 'Close', { duration: 5000 });
        return;
      }

      try {
        // Suspend session tracking during save to prevent re-triggering unsaved changes
        this.suspendSessionTracking = true;

        const cognitoUser = await this.authService.getCurrentUser();
        if (!cognitoUser) {
          this.snackBar.open('Not authenticated', 'Close', { duration: 3000 });
          return;
        }

        const userId = await this.authService.getUserEmail();
        if (!userId) {
          this.snackBar.open('Could not retrieve user email', 'Close', { duration: 3000 });
          return;
        }

        // Use AG Grid's official getState() API - captures EVERYTHING including column widths
        const gridState = this.gridApi.getState();

        const viewData = {
          gridState: gridState,  // New format using AG Grid's official state API
          columnDefs: this.selectedCols,  // Save the column definitions (which columns are visible)
          savedViewName: this.nameOfSavedView.trim(),
          // Keep old format for backward compatibility with existing views
          savedViewData: this.gridApi.getColumnState(),
          filterModel: gridState.filter?.filterModel || {}
        };

        // Save as named view using entityType.viewName format
        const viewId = `${this.entityType}.${this.nameOfSavedView.trim()}`;

        await lastValueFrom(
          this.viewsService.saveListView(environment.org, userId, viewId, viewData)
        );

        this.snackBar.open(`View "${this.nameOfSavedView}" saved successfully!`, 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });

        // Reset and close the dialog
        this.nameOfSavedView = null;
        this.showNameSavedView = false;

        // Clear unsaved changes flag since we just saved
        this.clearSessionState();

        // Refresh the list of saved views
        await this.loadUserViews();
      } catch (error) {
        console.error('Error saving named view:', error);
        this.snackBar.open('Failed to save named view', 'Close', { duration: 5000 });
      } finally {
        // Always resume session tracking, even if save fails
        this.suspendSessionTracking = false;
      }
    }

    /**
     * Loads a saved named view and applies it to the grid.
     * @param savedView The view data object containing columnState and filterModel
     */
    async loadNamedView(savedView: any) {
      if (!this.gridApi) {
        this.snackBar.open('Grid not ready', 'Close', { duration: 3000 });
        return;
      }

      if (!savedView) {
        console.error('No view data provided');
        return;
      }

      try {
        // Cancel any pending session tracking restoration from previous operations (like autoSizeAll)
        if (this.sessionTrackingRestoreTimeout) {
          clearTimeout(this.sessionTrackingRestoreTimeout);
          this.sessionTrackingRestoreTimeout = null;
        }

        // Set flag to prevent firstDataRendered listener from restoring old session state
        this.isLoadingNamedView = true;

        this.waitingForServer = true;

        // Check if this view uses the new gridState format or old columnState format
        if (savedView.gridState) {
          // NEW FORMAT: Use AG Grid's official setState() API

          // IMPORTANT: setState() doesn't change columnDefs (visible columns)
          // We need to restore the columnDefs from the saved view

          // PREFERRED: Use saved columnDefs if available (newest format)
          if (savedView.columnDefs && Array.isArray(savedView.columnDefs)) {
            // Get available columns from the component (these have valueGetters and other functions)
            const availableColumns = (this as any).availableColumns || [];

            // Restore function properties (valueGetter, valueFormatter, etc.) from availableColumns
            const restoredColumnDefs = savedView.columnDefs.map((savedCol: any) => {
              const { width, ...colWithoutWidth } = savedCol; // Remove width (comes from gridState)

              // Find the original column definition
              const originalCol = availableColumns.find((col: any) => col.colId === savedCol.colId);

              if (originalCol) {
                // Only restore function properties, don't touch anything else
                const restoredCol = { ...colWithoutWidth };
                if (typeof originalCol.valueGetter === 'function') {
                  restoredCol.valueGetter = originalCol.valueGetter;
                }
                if (typeof originalCol.valueFormatter === 'function') {
                  restoredCol.valueFormatter = originalCol.valueFormatter;
                }
                if (typeof originalCol.valueSetter === 'function') {
                  restoredCol.valueSetter = originalCol.valueSetter;
                }
                if (typeof originalCol.cellRenderer === 'function') {
                  restoredCol.cellRenderer = originalCol.cellRenderer;
                }
                // Restore string cellRenderer (like 'agGroupCellRenderer' for master/detail expand arrow)
                // This ensures saved views preserve the expand arrow functionality
                if (typeof originalCol.cellRenderer === 'string' && !restoredCol.cellRenderer) {
                  restoredCol.cellRenderer = originalCol.cellRenderer;
                }
                return restoredCol;
              }

              return colWithoutWidth;
            });

            console.log('[loadNamedView] Restored function properties from availableColumns');

            // IMPORTANT: Reorder columns to match gridState.columnOrder before applying
            const orderedColIds = savedView.gridState.columnOrder?.orderedColIds;
            let finalColumnDefs = restoredColumnDefs;
            if (orderedColIds && Array.isArray(orderedColIds)) {
              // Reorder restoredColumnDefs to match orderedColIds
              finalColumnDefs = orderedColIds
                .map((colId: string) => restoredColumnDefs.find((col: any) => col.colId === colId))
                .filter((col: any) => col !== undefined);
            }

            this.selectedCols = finalColumnDefs;
            this.gridApi.setGridOption('columnDefs', finalColumnDefs);

            // CRITICAL: Wait for columnDefs to be applied before setting state
            // setGridOption is async and recreates columns, so we need to wait longer
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          // FALLBACK: Extract column order from gridState
          else {
            const orderedColIds = savedView.gridState.columnOrder?.orderedColIds;
            if (orderedColIds && Array.isArray(orderedColIds)) {
              // Get all available columns from the component
              const availableColumns = (this as any).availableColumns || this.selectedCols || [];

              // Filter and reorder columns to match the saved view
              const colsToShow = orderedColIds
                .map((colId: string) => availableColumns.find((col: any) => col.colId === colId))
                .filter((col: any) => col !== undefined);

              // Update the selected columns
              this.selectedCols = colsToShow;

              // Apply the new column definitions to the grid
              this.gridApi.setGridOption('columnDefs', colsToShow);

              // CRITICAL: Wait for columnDefs to be applied before setting state
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }

          // Now apply the full grid state (order, widths, filters, etc.)
          this.gridApi.setState(savedView.gridState);
        } else {
          // OLD FORMAT: Fall back to manual column state for backward compatibility
          const columnState = savedView.columnState || savedView.savedViewData;

          if (columnState) {
            // Just apply the column state directly - no need to mess with columnDefs
            this.gridApi.applyColumnState({
              state: columnState,
              applyOrder: true
            });
          }

          // Apply filter model if exists
          if (savedView.filterModel) {
            this.gridApi.setFilterModel(savedView.filterModel);
          }
        }

        // Suspend session tracking IMMEDIATELY to prevent any auto-saves during view load
        this.suspendSessionTracking = true;

        // Remove any pending firstDataRendered listeners that might restore old session state
        // This prevents session state from being loaded after we've cleared it
        if (this.gridApi) {
          this.gridApi.removeEventListener('firstDataRendered');
        }

        // NEVER auto-resize saved views - load them exactly as the user saved them
        // Re-enable session tracking after a delay to allow all grid events to fire while suspended
        // AG Grid queues up columnResized and columnMoved events during view load that fire asynchronously
        this.sessionTrackingRestoreTimeout = setTimeout(() => {
          // Save the loaded view's state to session storage so it persists across navigation.
          // This replaces the old clearSessionState() call which caused the bug where
          // navigating away after loading a saved view would lose the view.
          this.saveSessionState();
          this.suspendSessionTracking = false;
          this.isLoadingNamedView = false;
          this.sessionTrackingRestoreTimeout = null;
        }, 1000);

        // Update filter status AFTER filters have been applied
        // Use setTimeout to ensure the grid has processed the filter changes
        setTimeout(() => {
          this.updateFiltersAppliedStatus();
        }, 100);

        this.waitingForServer = false;
        this.loadSavedReportVisible = false;

        this.snackBar.open('View loaded successfully', 'Close', {
          duration: 2000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      } catch (error) {
        console.error('Error loading view:', error);
        this.waitingForServer = false;
        this.isLoadingNamedView = false; // Clear flag on error
        this.suspendSessionTracking = false; // Re-enable tracking on error
        this.snackBar.open('Failed to load view', 'Close', { duration: 5000 });
      }
    }

    /**
     * Determines if columns should be auto-sized based on saved column state.
     * Returns true if NO columns have custom widths (user never resized).
     * Returns false if ANY column has a custom width (user customized, respect their intent).
     *
     * @param columnState - The saved column state from the view
     * @returns true if columns should be auto-sized, false otherwise
     */
    private shouldAutoSizeColumns(columnState: any[]): boolean {
      if (!columnState || columnState.length === 0) {
        return true;
      }

      // If ANY column has a saved width, the user customized columns
      const hasAnyCustomWidth = columnState.some(col => col.width !== undefined);

      return !hasAnyCustomWidth;
    }

    // *************************
    // *** AG GRID RESOURCES ***
    // *************************

    resetState(gridColumnApi, colState) {
      gridColumnApi.applyColumnState({
        state: colState,
        applyOrder: true,
      });
    }
    

  showSaveNamedView() {
    this.showNameSavedView = !this.showNameSavedView;
  }





    selectionChanged(event) {

      // console.log('current column state: ', this.globalDataService.coiColumnState);
      this.gridApi.setColumnDefs(this.selectedCols);
      this.columnApi.setColumnState(this.selectedCols);
  
      this.gridApi.sizeColumnsToFit();
    }


    returnColumnsFromUserView(userView, allColumns) {

      let userSelectedColIds = userView.map(col => col.colId);
      let selectedCols = allColumns.filter( column => {
        let foundColumn = userSelectedColIds.indexOf(column.colId);
        if (foundColumn != -1) {
          return true;
        }
        return false;
      })

      return selectedCols;
    }


    customizeColumns() {
      this.selectedCols = [];
      this.customizeColumnsUi = !this.customizeColumnsUi;
    }


    async loadDefaultView(userView, gridApi, columnApi) {

      columnApi.setColumnState(userView);

      // BUGBBUG:  Use this when you implement filter saving
      if (userView.filterModel) {
        this.gridApi.setFilterModel(userView.filterModel);
      }

      gridApi.sizeColumnsToFit();

    }


    async loadToolbarNamedView(savedView, gridApi, columnApi) {

      this.waitingForServer = true;
  
      this.selectedCols = await this.returnColumnsFromUserView(savedView.savedViewData, this.allColumns);
    
      if (savedView.filterModel) {
        gridApi.setFilterModel(savedView.filterModel);
      }

      columnApi.setColumnState(savedView);

      if (savedView.filterModel) {
        gridApi.setFilterModel(savedView.filterModel);
      }

      gridApi.sizeColumnsToFit();
  
      this.waitingForServer = false;
    }    



    /**
     * Loads all user views (default and named) from the API.
     * Populates this.savedUserViews with the named views for this entity type.
     */
    async loadUserViews() {
      if (!this.entityType) {
        console.error('EntityList: entityType not set in derived class');
        return;
      }

      try {
        const cognitoUser = await this.authService.getCurrentUser();
        if (!cognitoUser) {
          return;
        }

        const userId = await this.authService.getUserEmail();
        if (!userId) {
          console.error('Could not retrieve user email');
          return;
        }

        const userViewData = await lastValueFrom(
          this.viewsService.getUserView(userId)
        );

        // Migration-safe: view data may live at .userData.userData (legacy) or
        // .userData / root once the backend flattens the wrapper.
        const userData = unwrapUserData<any>(userViewData);
        if (userData) {

          // The inner object is keyed by entity type:
          //   userData[entityType] = default view
          //   userData[`${entityType}_named_views`] = array of named views

          const namedViewsKey = `${this.entityType}_named_views`;

          // Get named views for this entity type
          if (userData[namedViewsKey]) {
            // Map savedViewName to name for the dialog
            this.savedUserViews = userData[namedViewsKey].map((view: any) => ({
              ...view,
              name: view.savedViewName || view.name  // Support both savedViewName (from DB) and name
            }));
          } else {
            this.savedUserViews = [];
          }

          // Get default view for this entity type
          if (userData[this.entityType]) {
            this.savedNamedViews = userData[this.entityType];
          }
        }
      } catch (error) {
        console.error('Error loading user views:', error);
        this.savedUserViews = [];
      }
    }

    /**
     * Gets the user's default view for this entity type.
     * @returns The default view data or null
     */
    async getUserViewByType(entityType: string) {
      try {
        const cognitoUser = await this.authService.getCurrentUser();
        if (!cognitoUser) {
          return null;
        }

        const userId = await this.authService.getUserEmail();
        if (!userId) {
          console.error('Could not retrieve user email');
          return null;
        }

        const userViewData = await lastValueFrom(
          this.viewsService.getUserView(userId)
        );

        // Migration-safe: view data may live at .userData.userData (legacy) or
        // .userData / root once the backend flattens the wrapper.
        const userData = unwrapUserData<any>(userViewData);
        if (userData) {
          // userData[entityType] = default view
          if (userData[entityType]) {
            return userData[entityType];
          }
        }

        return null;
      } catch (error) {
        console.error('Error getting user view by type:', error);
        return null;
      }
    }

    /**
     * Gets the user's named views for this entity type.
     * @returns Array of named views or null
     */
    async getNamedUserView(entityType: string) {
      try {
        const cognitoUser = await this.authService.getCurrentUser();
        if (!cognitoUser) {
          return null;
        }

        const userId = await this.authService.getUserEmail();
        if (!userId) {
          console.error('Could not retrieve user email');
          return null;
        }

        const userViewData = await lastValueFrom(
          this.viewsService.getUserView(userId)
        );

        // Migration-safe: view data may live at .userData.userData (legacy) or
        // .userData / root once the backend flattens the wrapper.
        const userData = unwrapUserData<any>(userViewData);
        if (userData) {
          // userData[`${entityType}_named_views`] = array of named views
          const namedViewsKey = `${entityType}_named_views`;
          if (userData[namedViewsKey]) {
            return userData[namedViewsKey];
          }
        }

        return null;
      } catch (error) {
        console.error('Error getting named user views:', error);
        return null;
      }
    }

    /**
     * Deletes a custom named view.
     * @param viewName The name of the view to delete
     */
    async deleteNamedView(viewName: string) {
      if (!this.entityType) {
        console.error('EntityList: entityType not set in derived class');
        this.snackBar.open('Configuration error: entity type not set', 'Close', { duration: 5000 });
        return;
      }

      try {
        const cognitoUser = await this.authService.getCurrentUser();
        if (!cognitoUser) {
          this.snackBar.open('Not authenticated', 'Close', { duration: 3000 });
          return;
        }

        const userId = await this.authService.getUserEmail();
        if (!userId) {
          this.snackBar.open('Could not retrieve user email', 'Close', { duration: 3000 });
          return;
        }

        await lastValueFrom(
          this.viewsService.deleteCustomView(environment.org, userId, viewName, this.entityType)
        );

        this.snackBar.open(`View "${viewName}" deleted successfully`, 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });

        // Refresh the list of saved views
        await this.loadUserViews();
      } catch (error) {
        console.error('Error deleting view:', error);
        this.snackBar.open('Failed to delete view', 'Close', { duration: 5000 });
      }
    }

    /**
     * Getter for accessing user views
     */
    async getUserViews() {
      await this.loadUserViews();
      return this.savedUserViews;
    }

    /**
     * Opens a dialog to load a saved view.
     * User can select from their saved named views.
     */
    async openSavedReportsDialog() {
      await this.loadUserViews();

      const dialogRef = this.dialog.open(LoadViewDialogComponent, {
        width: '500px',
        data: {
          savedViews: this.savedUserViews,
          entityType: this.entityType
        }
      });

      const selectedView = await lastValueFrom(dialogRef.afterClosed());

      if (selectedView) {
        await this.loadNamedView(selectedView);
      }
    }

    /**
     * Opens a dialog to delete saved views.
     * User can select which view to delete.
     */
    async openDeleteSavedViewsDialog() {
      await this.loadUserViews();

      const dialogRef = this.dialog.open(DeleteViewDialogComponent, {
        width: '500px',
        data: {
          savedViews: this.savedUserViews,
          entityType: this.entityType
        }
      });

      const viewNameToDelete = await lastValueFrom(dialogRef.afterClosed());

      if (viewNameToDelete) {
        await this.deleteNamedView(viewNameToDelete);
      }
    }

    /**
     * Opens a dialog to share a saved view with another user.
     * User can select which view to share and which user to share it with.
     * Requires Admin or Editor role.
     */
    async openShareViewDialog() {
      // Check if user has permission (not Viewer)
      if (this.userRole === 'Viewer') {
        this.snackBar.open('You do not have permission to share views', 'Close', { duration: 3000 });
        return;
      }

      // Load the current user's saved views
      await this.loadUserViews();

      // Get current user's email
      const currentUserEmail = await this.authService.getUserEmail();
      if (!currentUserEmail) {
        this.snackBar.open('Could not retrieve user email', 'Close', { duration: 3000 });
        return;
      }

      const dialogRef = this.dialog.open(ShareViewComponent, {
        width: '600px',
        data: {
          savedViews: this.savedUserViews,
          entityType: this.entityType,
          currentUserEmail: currentUserEmail
        }
      });

      const result = await lastValueFrom(dialogRef.afterClosed());

      if (result && result.success) {
        console.log('View shared successfully');
      }
    }



    async columnIsFromAccordion(field) {

      switch (field) {
        case 'grantDocumentNumber':
        case 'grantFundNumber':
        case 'grantFundNum':
        case 'grantRestrictionsType':
        case 'grantProgramType':
        case 'grantProgramKey':
        case 'grantProgramNumber':
        case 'grantUse':
        case 'revisedAwardValue':
        case 'apRetentionBonus':
        case 'apRetentionBonusPeriod':
        case 'apSalaryAfterRetention':
        case 'apArRunoutPeriod':
        case 'apDateRetentionBonusPaid':
        case 'apDailyPatientHours':
        case 'apPerHourRate':
        case 'apCompensationStructure':
        case 'apCompensationDetails':
        case 'apDateChangeFromGuaranteeToProd':
        case 'apGuaranteePayAmount':
        case 'apGuaranteedPayAmount':
        case 'apNppSupervisionServices':
        case 'apNppSupervisionCompensation':
        case 'apMmdOrCmo':
        case 'apMedicalDirectorFee':
        case 'apCmoFee':
        case 'apPiusTotalAward':
        case 'apPiuAwardDate':
        case 'apSignOnBonusAmount':
        case 'apBonusPaybackPeriod':
        case 'apRelocationAmount':
        case 'apWRVU':
        case 'apSalary':
        case 'apTeachingCompensation':
        case 'valuePatient':
        case 'valueHour':
        case 'valueDay':
        case 'valueMonth':
        case 'valueResidentPerYear':
        case 'valuePerDiem':
        case 'valueEncounter':
        case 'valueDelivery':
        case 'valueWorkshop':
        case 'valueClinic':
        case 'valueClass':
        case 'valueEvent':
        case 'valueProgram':
        case 'valueStudentPerWeek':
        case 'valueStudentPerMonth':
        case 'acuteCareComp':
        case 'acuteCareBonus':
        case 'acuteCareCritical':
        case 'acuteCareHourlyRate':
        case 'acuteCareMeanFMV':
        case 'acuteCareRangeFMV':
        case 'acuteCareSpecialty':
        case 'acuteCareKeyTerms':
        case 'acuteCareSquareFootage':
        case 'leaseStartDate':
        case 'leaseEndDate':
        case 'leaseEarlyTermClause':
        case 'leaseEarlyTermReminderDate':
        case 'leaseOptionToPurchase':
        case 'leaseFixedRent':
        case 'leaseRentMonthlyValue':
        case 'leaseParkingRates':
        case 'leaseParkingMonthlyValue':
        case 'leaseStorageRates':
        case 'leaseStorageMonthlyValue':
        case 'leaseRentRateEscalation':
        case 'leaseRentEscalationMonthlyValue':
        case 'leaseParkingRateEscalation':
        case 'leaseParkingEscalationMonthlyValue':
        case 'leaseStorageRateEscalation':
        case 'leaseStorageEscalationMonthlyValue':
        case 'hrNumDaysPerWeek':
        case 'hrWhichDaysPerWeek':
        case 'hrHoursPerWeek':
        case 'hrProfessionalServices':
        case 'hrCmeDays':
        case 'hrCmeAllowanceAmount':
        case 'hrCmeDues':
        case 'hrSickDaysPerYear':
        case 'hrVacationDaysPerYear':
        case 'hrCombinedSickVacationDays':
        case 'hrPtoTimeOff':
        case 'hrGroup':
        case 'hrProduct':
        case 'hrLawsonNumber':
        case 'hrDnv':
        case 'hrOtherInfo':
        case 'fmvRenewalDate':
        case 'fmvCompletedDate':

          // console.log('found field: ', field)

          return true;
      
        default:
          return false;
      }
    }




    exportCSV() {

      let params =  {
                        processCellCallback: this.formattingFunction,
                        fileName: 'BDA_export.csv'
                    }
  
      this.gridApi.exportDataAsCsv(params);
    }
  
    exportExcel() {
  
      let excelParams = {
            processCellCallback: this.formattingFunction,
            fileName: 'BDA_export.xls'
        }
  
      this.gridApi.exportDataAsExcel(excelParams);
    }


    /**
     * Formatting function for Excel and CSV exports.
     * Automatically formats any field with "date" in its name to USA format (MM/DD/YYYY).
     * Also handles fields with "Date" in the name (case-insensitive).
     */
    formattingFunction (params) {
        if (params.column && params.value) {
          const colId = params.column.getColId();

          // Check if the column ID contains "date" (case-insensitive)
          // This will match: effectiveDate, expirationDate, createdDate, dateAdded, etc.
          if (colId && colId.toLowerCase().includes('date')) {
            const momentObject = moment(params.value);

            if (momentObject.isValid()) {
              return momentObject.format('MM/DD/YYYY');
            } else {
              // If it's not a valid date, return the original value
              return params.value;
            }
          }
        }

        // Return the original value for non-date fields
        return params.value;
      }


}
